import React from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { ResidentAppError } from '../../common/error'
import { Contract, FeatureFlag, UserMetadata } from '../../common/validations'
import { TContact } from '../../common/validations/contractContacts'
import { makeContractFrontPageURL } from '../router/routePath'
import { getDefaultContract, orderContracts } from '../utils/orderContracts'

import { useAPIGetBulletinsSuspense } from 'api/apiHooks/useAPIGetBulletins'
import { useAPIGetContractSuspense } from 'api/apiHooks/useAPIGetContract'
import { useAPIGetContractContactsSuspense } from 'api/apiHooks/useAPIGetContractContacts'
import { useAPIGetContractsSuspense } from 'api/apiHooks/useAPIGetContracts'
import { useAPIGetFeatureFlagsSuspense } from 'api/apiHooks/useAPIGetFeatureFlags'
import { useAPIGetUserMetadataSuspense } from 'api/apiHooks/useAPIGetUserMetadata'
import { useAPIGetUserNotificationsSuspense } from 'api/apiHooks/useAPIGetUserNotifications'
import { useAPIGetUserProfileSuspense } from 'api/apiHooks/useAPIGetUserProfile'

interface Data {
  featureFlags: FeatureFlag
  contacts: TContact[]
  contracts: Contract[]
  userMetadata: UserMetadata
}

export const DataLoader = ({
  children,
}: {
  children: (data: Data) => React.ReactNode
}) => {
  const { t } = useTranslation('errors')
  const { contractId: currentContractId } = useParams()

  const { data: featureFlags } = useAPIGetFeatureFlagsSuspense()
  const { data: contracts } = useAPIGetContractsSuspense()
  const orderedContracts = orderContracts(contracts)

  if (!contracts || contracts.length === 0) {
    throw ResidentAppError.ClientError('NO_ACTIVE_CONTRACTS', {
      message: t('other.noActiveContracts'),
    })
  }

  const fallbackContract = getDefaultContract(contracts)

  const contractInURL = orderedContracts.find(
    (contract) => contract.uuid === currentContractId
  )

  const defaultContract = contractInURL || fallbackContract

  const { data: contacts } = useAPIGetContractContactsSuspense(
    defaultContract.uuid
  )

  useAPIGetContractSuspense(defaultContract.uuid)
  useAPIGetBulletinsSuspense(defaultContract.uuid)

  useAPIGetUserNotificationsSuspense()
  useAPIGetUserProfileSuspense()

  const { data: userMetadata } = useAPIGetUserMetadataSuspense()

  if (!currentContractId) {
    return (
      <Navigate to={makeContractFrontPageURL(defaultContract.uuid)} replace />
    )
  }

  return children({
    featureFlags,
    contacts,
    contracts: orderedContracts,
    userMetadata,
  })
}
